import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { useSizeTypes } from 'contexts';
import { useFleetFiles } from 'hooks';
import { FleetLinkError } from 'services/fleet-files';
import { EquipmentType } from 'types/damage';
import * as FleetFile from 'types/fleet-files';

import {
  draftFrom,
  draftProblem,
  emptyDraft,
  SizeTypeDraft,
  targetChoices,
  toCreate,
  toUpdate
} from './size-type-mapping-draft';

export type SizeTypeFormMode = { kind: 'edit'; row: FleetFile.SizeType } | { kind: 'create'; code?: number };

interface Props {
  mode: SizeTypeFormMode;
  onSaved: (row: FleetFile.SizeType) => void;
  onCancel: () => void;
  /* re-reads one row by provider code, after someone else saved it first */
  reloadRow: (code: number) => Promise<FleetFile.SizeType | undefined>;
  /* the server answered 409: what the page shows behind this dialog is out of date */
  onStale: () => void;
}

/*
 * Edits one crosswalk row's suggestion, or adds a provider code.
 *
 * What a row holds is a SUGGESTION to the gate user, who is responsible for entering what is really
 * in the yard. A save resolves with the committed row or shows the server's reason in place and keeps
 * the admin's edits; success is only reported by the page after a committed save (F09 contract).
 */
export const SizeTypeMappingForm: FC<Props> = ({ mode, onSaved, onCancel, reloadRow, onStale }) => {
  const { sizeTypeList } = useSizeTypes();
  const { updateSizeTypeMapping, createSizeTypeMapping } = useFleetFiles();

  const [row, setRow] = useState<FleetFile.SizeType | undefined>(mode.kind === 'edit' ? mode.row : undefined);
  const [draft, setDraft] = useState<SizeTypeDraft>(() => (mode.kind === 'edit' ? draftFrom(mode.row) : emptyDraft()));
  const [code, setCode] = useState<string>(mode.kind === 'create' && mode.code ? `${mode.code}` : '');
  const [description, setDescription] = useState('');
  const [iso, setIso] = useState('');
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  /* a ref, not state: two clicks in one frame both see `saving === false` in their closures */
  const savingRef = useRef(false);

  const choices = useMemo(
    () => targetChoices(sizeTypeList, draft.equipmentType, row?.targetSizeType?.id),
    [sizeTypeList, draft.equipmentType, row?.targetSizeType?.id]
  );
  const problem = draftProblem(draft);
  const createIncomplete = mode.kind === 'create' && (!(Number(code) > 0) || !description.trim());

  const setTarget = (value: string) => {
    const targetId = value ? Number(value) : null;
    const target = sizeTypeList.find(o => o.id === targetId);
    /* choosing a target decides the row; its class comes with it unless one is already set */
    setDraft(d => ({
      ...d,
      targetId,
      noTarget: targetId ? false : d.noTarget,
      equipmentType: d.equipmentType ?? target?.equipmentType ?? null
    }));
  };

  const save = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const saved =
        mode.kind === 'edit' && row
          ? await updateSizeTypeMapping(row.id, toUpdate(draft, row))
          : await createSizeTypeMapping(toCreate(Number(code), description, iso, draft));
      onSaved(saved);
    } catch (e) {
      const conflict = e instanceof FleetLinkError && e.status === 409;
      setError(e instanceof Error ? e.message : 'The mapping could not be saved.');
      setStale(conflict && mode.kind === 'edit');
      /*
       * Another admin saved this row, or added this code, first. The grid behind the dialog still
       * shows the old picture, and would keep showing it after "Reload this row" or Cancel.
       */
      if (conflict) onStale();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [mode.kind, row, draft, code, description, iso, updateSizeTypeMapping, createSizeTypeMapping, onSaved, onStale]);

  const reload = useCallback(async () => {
    if (!row) return;
    const fresh = await reloadRow(row.code);
    if (fresh) {
      setRow(fresh);
      setDraft(draftFrom(fresh));
      setError('');
      setStale(false);
    }
  }, [row, reloadRow]);

  return (
    <div className="dialog-body" data-testid="size-type-mapping-form">
      {mode.kind === 'edit' && row ? (
        <div>
          <h3>Fleet size type code {row.code}</h3>
          <p className="margin-top-8px mb-0">{row.description || '(no provider description)'}</p>
          {!!row.iso && <p className="text-dark mb-0">ISO aliases: {row.iso}</p>}
          {!!row.legacySizeType && (
            <p className="text-dark mb-0">Legacy value (never suggested): {row.legacySizeType}</p>
          )}
        </div>
      ) : (
        <div>
          <h3>Add provider size type code</h3>
          <label htmlFor="stm-code" className="d-block margin-top-16px">
            Provider code
          </label>
          <input
            id="stm-code"
            className="w-100"
            type="number"
            min={1}
            value={code}
            onChange={({ target }) => setCode(target.value)}
          />
          <label htmlFor="stm-description" className="d-block margin-top-16px">
            Provider description
          </label>
          <input
            id="stm-description"
            className="w-100"
            type="text"
            value={description}
            onChange={({ target }) => setDescription(target.value)}
          />
          <label htmlFor="stm-iso" className="d-block margin-top-16px">
            ISO aliases (optional)
          </label>
          <input
            id="stm-iso"
            className="w-100"
            type="text"
            value={iso}
            onChange={({ target }) => setIso(target.value)}
          />
        </div>
      )}

      <label htmlFor="stm-class" className="d-block margin-top-16px">
        Equipment class
      </label>
      <select
        id="stm-class"
        className="w-100"
        value={draft.equipmentType ?? ''}
        onChange={({ target }) =>
          setDraft(d => ({ ...d, equipmentType: (target.value || null) as EquipmentType | null }))
        }
      >
        <option value="">Not reviewed</option>
        {Object.values(EquipmentType).map(type => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      <label htmlFor="stm-target" className="d-block margin-top-16px">
        Suggest size type
      </label>
      <select
        id="stm-target"
        className="w-100"
        value={draft.targetId ?? ''}
        onChange={({ target }) => setTarget(target.value)}
      >
        <option value="">No suggestion</option>
        {choices.map(o => (
          <option key={o.id} value={o.id}>
            {o.code} — {o.label}
            {o.active ? '' : ' (retired)'}
          </option>
        ))}
      </select>

      <div className="d-flex align-items-center margin-top-16px">
        <input
          id="stm-no-target"
          type="checkbox"
          checked={draft.noTarget}
          disabled={!!draft.targetId}
          onChange={({ target }) => setDraft(d => ({ ...d, noTarget: target.checked }))}
        />
        <label htmlFor="stm-no-target" className="margin-left-8px mb-0">
          No single size type: the gate user always picks
        </label>
      </div>

      <label htmlFor="stm-note" className="d-block margin-top-16px">
        Review note{draft.noTarget && !draft.targetId ? ' (required: shown to the gate user)' : ''}
      </label>
      <textarea
        id="stm-note"
        className="w-100"
        value={draft.reviewNote}
        onChange={({ target }) => setDraft(d => ({ ...d, reviewNote: target.value }))}
      />

      {!!(error || problem) && (
        <p className="text-danger margin-top-16px mb-0" role="alert">
          {error || problem}
        </p>
      )}
      {stale && (
        <button type="button" className="btn-outline margin-top-8px" onClick={reload}>
          Reload this row
        </button>
      )}

      <div className="d-flex justify-content-between margin-top-16px">
        <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn-primary" disabled={saving || !!problem || createIncomplete} onClick={save}>
          <span>{saving ? 'Saving…' : 'Save'}</span>
        </button>
      </div>
    </div>
  );
};
